const PUSH_TRAIL_DATASET = "pharmacy_pa_requests";
const PUSH_TRAIL_TABLE = "sftp_push_trail";

/** A case is pushed a handful of times at most; 100 is a safety ceiling, not a page. */
const MAX_PUSHES_PER_CASE = 100;

export interface PushTrailRequestParams {
  /** BigQuery org id — the backend ANDs this into every query, we cannot widen it. */
  orgId: string;
  /** Input file the case arrived in; half of the trail's business key. */
  inputFilename: string;
  /** `seq` within the input file; the other half of the business key. */
  caseSeq?: string | null;
  /** Per-case fallback when `seq` is missing on the row. */
  identifier?: string | null;
}

/**
 * Builds the generic BigQuery read request for one case's push trail.
 *
 * Pure, and kept apart from the fetch so the request contract can be asserted
 * without pulling in the auth stack.
 *
 * @input params - Org and business-key identifiers for the case
 * @return Record<string, unknown> | null: Request body, or null when the case cannot be keyed
 */
export const buildPushTrailRequestBody = (
  params: PushTrailRequestParams,
): Record<string, unknown> | null => {
  const inputFilename = params.inputFilename?.trim();
  // `seq` reaches us from untyped BigQuery rows and is sometimes numeric.
  const caseSeq = params.caseSeq?.toString().trim();
  const identifier = params.identifier?.trim();

  // Without a per-case key we would return every push for the whole input
  // file — a wrong answer, not a partial one. Better to show no trail.
  if (!inputFilename || (!caseSeq && !identifier)) {
    return null;
  }

  const filters = [
    { column: "input_filename", operator: "=", value: inputFilename },
    caseSeq
      ? { column: "case_seq", operator: "=", value: caseSeq }
      : { column: "identifier", operator: "=", value: identifier },
  ];

  return {
    org_id: params.orgId,
    dataset_name: PUSH_TRAIL_DATASET,
    table_name: PUSH_TRAIL_TABLE,
    filters,
    // Must be explicit: the request model defaults to `filename DESC`, which is
    // not a column on this table, and the backend rejects unknown order_by
    // columns with a 400. Ascending, so batch numbering follows push order.
    order_by: [{ column: "pushed_at", direction: "ASC" }],
    page: 1,
    page_size: MAX_PUSHES_PER_CASE,
  };
};
